<template>
  <div class="cohort-unit-selector">
    <sui-filter-bar :multiFilterData="table.filterList"
                    :multiFilterHelps="table.helps"
                    :multiFilterEvents="filterEvents"
                    :selectionInfo="filterSelectionInfo">
    </sui-filter-bar>
    <sui-save-blob :target="csv"></sui-save-blob>
    <div class="row">
      <div class="col-xs-12">
        <h3>Liste des services sélectionnés</h3>
        <sui-table :columns="table.columns"
                  :actions="table.actions"
                  :keyfield="table.keyfield"
                  :list="finalFilteredList"
                  :showActionBar="true"
                  :showFilter="false"
                  :caracteristicsBuilder="sc.caracteristicsBuilder"
                  @remove="askForRemoval"
                  title="Liste des données d'activité">
          <template slot="actions">
            <div class="form-group">
              <sui-checkbox v-model="showETypes"
                            type="results"
                            size="sm">
                Types d'établissements
              </sui-checkbox>
            </div>

            <div class="form-group">
              <sui-checkbox v-model="showBeds"
                            type="neoInfADI"
                            size="sm">
                Lits surveillés
              </sui-checkbox>
            </div>
            <div class="form-group">
              <sui-checkbox v-model="showUfs"
                            type="bactADI"
                            size="sm">
                Ufs
              </sui-checkbox>
            </div>
            <div class="form-group">
              <sui-checkbox v-model="showJh"
                            type="reaInfADI"
                            size="sm">
                JH
              </sui-checkbox>
            </div>
            <div class="form-group">
              <sui-checkbox v-model="showSea"
                            type="diaInfADI"
                            size="sm">
                Séances
              </sui-checkbox>
            </div>
            <div class="form-group">
              <sui-checkbox v-model="legacySelection"
                            type="warning"
                            size="sm">
                Ancienne sélection
              </sui-checkbox>
            </div>
          </template>
        </sui-table>
      </div>
    </div>
    <div class="row">
      <div class="col-xs-12">
        <h3>Jeux de données de la cohorte</h3>
        <p v-if="selectionDatasets != null">
          Les jeux de données de la cohorte ont été figés.
        </p>
        <p v-else>
          <btn @click="lockSelection"
             type="results"
             size="xl">
            Appliquer la sélection et créer les jeux de données pour la cohorte
          </btn>
        </p>
      </div>
    </div>
    <div class="filter-bar-padding">
    </div>
  </div>
</template>
<script>
import modules from '@/sui/data/modules'
import results from '@/sui/data/results'
import caracteristics from '@/sui/data/caracteristics'

import assert from '@/sui/tools/assert'
import { confirm } from '@/sui/tools/modal'
import distribution from '@/sui/tools/distribution'
import promise from '@/sui/tools/promise'
import regions from '@/sui/tools/regions'
import ice from '@/sui/tools/ice'
import { pushOrCreate } from '@/sui/tools/code'

import suiCheckbox from '@/sui/components/checkbox'
import suiFilterBar from '@/sui/components/filter/filterbar'
import suiSaveBlob from '@/sui/components/saveblob'
import suiTable from '@/sui/components/table'

import api from '@/sui/api/sheet'

import go from '@/gojs/go'

import clone from 'clone'
import stringify from 'csv-stringify'
import { mapGetters, mapState } from 'vuex'

function buildCaracteristics (list) {
  const protocols = modules.get(2024)
  const v = []
  for (let key in protocols) {
    const p = protocols[key]
    v.push(
      { code: key, value: p.label, color: p.color }
    )
  }

  const years = [
    { code: 0, value: 'Aucune' },
    { code: 1, value: '1 année' }
  ]

  const nb = 2026 - 2020
  for (let i = 2; i <= nb; ++i) {
    years.push({ code: i, value: `${i} années` })
  }

  const tables = [
    {
      title: 'Participation',
      key: 'who',
      open: true,
      list: [
        {
          f: 'type',
          t: `Établissements regroupés par type`,
          v: [
            { code: '', value: 'Aucun' },
            ...results.getEstablishementTypes()
          ]
        }
      ]
    },

    {
      title: 'Jeux de données',
      key: 'dset',
      open: true,
      list: [
        {
          f: 'mod',
          t: 'Modalité surveillées',
          v: v
        },
        {
          f: 'nbjh',
          t: 'Disciplines avec jh > 0 pour ...',
          v: years
        },
        {
          f: 'nbsea',
          t: 'Disciplines avec séances > 0 pour ...',
          v: years
        }
      ]
    }
  ]

  const tests = [
    {
      key: 'mod',
      type: 'rep',
      apply: (b, s) => b.increment(s.mod)
    },
    {
      key: 'nbjh',
      type: 'rep',
      apply: (b, s) => b.increment(s.nbjh)
    },
    {
      key: 'nbsea',
      type: 'rep',
      apply: (b, s) => b.increment(s.nbsea)
    },
    {
      key: 'type',
      type: 'rep',
      initialize: (b) => {
        b.cache = new Set()
      },
      apply: (b, s) => {
        if (b.cache.has(s.finess)) {
          return
        }
        b.increment(s.type)
        b.cache.add(s.finess)
      }
    }
  ]

  const source = distribution.extract(list, tests)
  const result = caracteristics.flatten.flatten(tables, source)
  return ice.freeze(result)
}

function buildColumn (e, f) {
  const cl = 'table-column-filter'
  return `${e} <span class='${cl}'>(${f})</span>`
}

function buildColumns () {
  const col = buildColumn
  return [
    {
      html: col('Finess', '$f'),
      field: 'finess'
    },
    {
      html: col('Nom', '$n'),
      field: 'name'
    },
    {
      html: col('Région', '$r'),
      field: 'region'
    },
    {
      html: col('Type', '$t'),
      field: 'type'
    },
    {
      html: col('Ville', '$v'),
      field: 'town'
    },
    {
      html: col('Modalité', '$m'),
      field: 'mod'
    },
    {
      html: col('Discipline', '$d'),
      field: 'disc'
    },
    {
      html: col('Lits<br><small>Établissement entier</small>', '$etb_lits'),
      field: 'etbbed'
    },
    {
      html: col('JH<br><small>Établissement entier</small>', '$etb_jh'),
      field: 'etbjh'
    },
    {
      html: col('Séances<br><small>Établissement entier</small>', '$etb_sea'),
      field: 'etbsea'
    },
    {
      html: col('Lits<br><small>Discipline toutes modalités</small>', '$disc_lits'),
      field: 'discbed'
    },
    {
      html: col('JH<br><small>Discipline toutes modalités</small>', '$disc_jh'),
      field: 'discjh'
    },
    {
      html: col('Séances<br><small>Discipline toutes modalités</small>', '$disc_sea'),
      field: 'discsea'
    },
    {
      html: col('Lits<br><small>Discipline pour cette modalité</small>', '$mod_lits'),
      field: 'nbbed'
    },
    {
      html: col('JH<br><small>Discipline pour cette modalité</small>', '$mod_jh'),
      field: 'nbjh'
    },
    {
      html: col('Séances<br><small>Discipline pour cette modalité</small>', '$mod_sea'),
      field: 'nbsea'
    }
  ]
}

const baseYearColumns = [
  {
    show: (vm) => vm.showUfs,
    title: y => `Ufs ${y}`,
    filter: y => `$uf${y}`,
    field: y => `ufs${y}Display`
  },
  {
    show: (vm) => vm.showBeds,
    title: y => `Lits surveillés ${y}`,
    filter: y => `$lits${y}`,
    field: y => `bed${y}`
  },
  {
    show: (vm) => vm.showJh,
    title: y => `JH ${y}`,
    filter: y => `$jh${y}`,
    field: y => `jh${y}`
  },
  {
    show: (vm) => vm.showSea,
    title: y => `Séances ${y}`,
    filter: y => `$sea${y}`,
    field: y => `sea${y}`
  },
  {
    show: (vm) => vm.showETypes,
    title: y => `Type ${y}`,
    filter: y => `$t${y}`,
    field: buildYearlyTypeKey
  }
]

function buildYearColumns (vm) {
  const yearColumns = []
  for (let i = 0; i !== baseYearColumns.length; ++i) {
    const b = baseYearColumns[i]
    if (b.show(vm)) {
      yearColumns.push(b)
    }
  }

  if (yearColumns.length === 0) {
    return []
  }

  const result = []
  const { dsets } = vm.data
  for (let y in dsets) {
    const c = dsets[y]
    if (c === '') {
      continue
    }

    for (let i = 0; i !== yearColumns.length; ++i) {
      const yc = yearColumns[i]
      result.push(
        {
          html: buildColumn(yc.title(y), yc.filter(y)),
          field: yc.field(y)
        }
      )
    }
  }
  return ice.freeze(result)
}

function buildYearFilterVariables (vm) {
  const result = []
  const { dsets } = vm.data
  for (let y in dsets) {
    result.push(
      { key: `uf${y}`, field: `ufs${y}Display`, name: `Ufs ${y}` },
      { key: `lits${y}`, field: `bed${y}`, name: `Lits surveillés ${y}` },
      { key: `jh${y}`, field: `jh${y}`, name: `Journées d'hospitalisation ${y}` },
      { key: `sea${y}`, field: `sea${y}`, name: `Séances ${y}` },
      { key: `t${y}`, field: buildYearlyTypeKey(y), name: `Type d'établissement ${y}` }
    )
  }
  return result
}

function buildYearlyDatasetKey (year) {
  return `dataset${year}`
}

function buildYearlyTypeKey (year) {
  return `estbt${year}`
}

function buildYearCSVColumns (vm) {
  const result = []
  const { dsets } = vm.data
  for (let y in dsets) {
    result.push(
      { key: `ufs${y}Display`, header: `Ufs ${y}` },
      { key: `bed${y}`, header: `Lits surveillés ${y}` },
      { key: `jh${y}`, header: `Journées d'hospitalisation ${y}` },
      { key: `sea${y}`, header: `Séances ${y}` },
      { key: buildYearlyTypeKey(y), header: `Type d'établissement ${y}` }
    )
  }
  return result
}

function disciplineHelp (code) {
  return { f: `($d: "${code}")`, t: code }
}

function buildTableLine (m, surv, disc, dsets) {
  const result = {
    mod: m,
    finess: surv.establishment.finess,
    name: surv.establishment.name,
    region: regions.getRegion(surv.establishment.finess).name,
    town: surv.establishment.town,
    disc: disc.Code,
    ut: disc.Index,
    nbjh: 0,
    nbbed: 0,
    nbsea: 0
  }

  for (let y in dsets) {
    result[`ufs${y}`] = []
    result[`ufs${y}Display`] = ''
  }
  return result
}

const sc = ice.freeze(
  {
    caracteristicsBuilder: buildCaracteristics,
    refByYear: [ 'bed', 'sea', 'jh' ],
    columns: buildColumns(),
    csvColumns: [
      { key: 'finess', header: 'Finess' },
      { key: 'name', header: 'Nom' },
      { key: 'region', header: 'Région' },
      { key: 'type', header: `Type d'établissement` },
      { key: 'town', header: 'Ville' },
      { key: 'mod', header: 'Modalité' },
      { key: 'disc', header: 'Discipline' },
      { key: 'etbbed', header: 'Lits - Établissement entier' },
      { key: 'etbjh', header: 'JH - Établissement entier' },
      { key: 'etbsea', header: 'Séances - Établissement entier' },
      { key: 'discbed', header: 'Lits - Discipline toutes modalités' },
      { key: 'discjh', header: 'JH - Discipline toutes modalités' },
      { key: 'discsea', header: 'Séances - Discipline toutes modalités' },
      { key: 'nbbed', header: 'Lits - Discipline pour cette modalité' },
      { key: 'nbjh', header: 'JH - Discipline pour cette modalité' },
      { key: 'nbsea', header: 'Séances - Discipline pour cette modalité' }
    ],

    filterVariables: [
      { key: 'f', field: 'finess', name: 'Numéro FINESS' },
      { key: 'n', field: 'name', name: 'Nom' },
      { key: 'r', field: 'region', name: 'Région' },
      { key: 't', field: 'type', name: `Type d'établissement` },
      { key: 'v', field: 'town', name: 'Ville' },
      { key: 'm', field: 'mod', name: 'Modalité' },
      { key: 'd', field: 'disc', name: 'Discipline' },
      { key: 'etb_lits', field: 'etbbed', name: 'Nombre de participations établissement (lits surveillés)' },
      { key: 'etb_jh', field: 'etbjh', name: 'Nombre de participations établissement (jh)' },
      { key: 'etb_sea', field: 'etbsea', name: 'Nombre de participations établissement (sea)' },
      { key: 'disc_lits', field: 'discbed', name: 'Nombre de participations discipline (lits surveillés)' },
      { key: 'disc_jh', field: 'discjh', name: 'Nombre de participations discipline (jh)' },
      { key: 'disc_sea', field: 'discsea', name: 'Nombre de participations discipline (sea)' },
      { key: 'mod_lits', field: 'nbbed', name: 'Nombre de participations discipline pour une modalité (lits surveillés)' },
      { key: 'mod_jh', field: 'nbjh', name: 'Nombre de participations discipline pour une modalité (jh)' },
      { key: 'mod_sea', field: 'nbsea', name: 'Nombre de participations discipline pour une modalité (sea)' }
    ],

    helps: [
      {
        title: 'Disciplines',
        list: [
          disciplineHelp(go.units.disciplines.AdRea.code),
          disciplineHelp(go.units.disciplines.AdDia.code),
          disciplineHelp(go.units.disciplines.NeoRea.code),
          disciplineHelp(go.units.disciplines.AdCan.code),
          disciplineHelp(go.units.disciplines.AdHem.code),
          disciplineHelp(go.units.disciplines.AdMed.code),
          disciplineHelp(go.units.disciplines.AdChir.code)
        ]
      },
      {
        title: `<strong>Établissement</strong> : participation sur plusieurs années, toutes disciplines et modalités confondues`,
        list: [
          {
            f: '($etb_jh >= 2 et $etb_lits >= 2)',
            t: 'JH : au moins 2 années'
          },
          {
            f: '($etb_jh >= 3 et $etb_lits >= 3)',
            t: 'JH : au moins 3 années'
          },
          {
            f: '($etb_jh >= 4 et $etb_lits >= 4)',
            t: 'JH : au moins 4 années'
          },
          {
            f: '($etb_jh >= 5 et $etb_lits >= 5)',
            t: 'JH : au moins 5 années'
          },
          {
            f: '($etb_jh >= 6 et $etb_lits >= 6)',
            t: 'JH : au moins 6 années'
          },
          {
            f: '($etb_sea >= 2 et $etb_lits >= 2)',
            t: 'Séances : au moins 2 années'
          },
          {
            f: '($etb_sea >= 3 et $etb_lits >= 3)',
            t: 'Séances : au moins 3 années'
          },
          {
            f: '($etb_sea >= 4 et $etb_lits >= 4)',
            t: 'Séances : au moins 4 années'
          },
          {
            f: '($etb_sea >= 5 et $etb_lits >= 5)',
            t: 'Séances : au moins 5 années'
          },
          {
            f: '($etb_sea >= 6 et $etb_lits >= 6)',
            t: 'Séances : au moins 6 années'
          }
        ]
      },
      {
        title: `<strong>Discipline</strong> : participation sur plusieurs années, toutes modalité confondues`,
        list: [
          {
            f: '($disc_jh >= 2 et $disc_lits >= 2)',
            t: 'JH : au moins 2 années'
          },
          {
            f: '($disc_jh >= 3 et $disc_lits >= 3)',
            t: 'JH : au moins 3 années'
          },
          {
            f: '($disc_jh >= 4 et $disc_lits >= 4)',
            t: 'JH : au moins 4 années'
          },
          {
            f: '($disc_jh >= 5 et $disc_lits >= 5)',
            t: 'JH : au moins 5 années'
          },
          {
            f: '($disc_jh >= 6 et $disc_lits >= 6)',
            t: 'JH : au moins 6 années'
          },
          {
            f: '($disc_sea >= 2 et $disc_lits >= 2)',
            t: 'Séances : au moins 2 années'
          },
          {
            f: '($disc_sea >= 3 et $disc_lits >= 3)',
            t: 'Séances : au moins 3 années'
          },
          {
            f: '($disc_sea >= 4 et $disc_lits >= 4)',
            t: 'Séances : au moins 4 années'
          },
          {
            f: '($disc_sea >= 5 et $disc_lits >= 5)',
            t: 'Séances : au moins 5 années'
          },
          {
            f: '($disc_sea >= 6 et $disc_lits >= 6)',
            t: 'Séances : au moins 6 années'
          }
        ]
      },
      {
        title: `<strong>Discipline + modalité</strong> : participation sur plusieurs années à la même modalité`,
        list: [
          {
            f: '($mod_jh >= 2 et $mod_lits >= 2)',
            t: 'JH : au moins 2 années'
          },
          {
            f: '($mod_jh >= 3 et $mod_lits >= 3)',
            t: 'JH : au moins 3 années'
          },
          {
            f: '($mod_jh >= 4 et $mod_lits >= 4)',
            t: 'JH : au moins 4 années'
          },
          {
            f: '($mod_jh >= 5 et $mod_lits >= 5)',
            t: 'JH : au moins 5 années'
          },
          {
            f: '($mod_jh >= 6 et $mod_lits >= 6)',
            t: 'JH : au moins 6 années'
          },
          {
            f: '($mod_sea >= 2 et $mod_lits >= 2)',
            t: 'Séances : au moins 2 années'
          },
          {
            f: '($mod_sea >= 3 et $mod_lits >= 3)',
            t: 'Séances : au moins 3 années'
          },
          {
            f: '($mod_sea >= 4 et $mod_lits >= 4)',
            t: 'Séances : au moins 4 années'
          },
          {
            f: '($mod_sea >= 5 et $mod_lits >= 5)',
            t: 'Séances : au moins 5 années'
          },
          {
            f: '($mod_sea >= 6 et $mod_lits >= 6)',
            t: 'Séances : au moins 6 années'
          }
        ]
      }
    ]
  }
)

export default {
  name: 'cohort-unit-selector',

  props: {
    data: {
      type: Object,
      default: null
    }
  },

  data: function () {
    return {
      sc: sc,
      csv: null,

      datasetsLoaded: null,
      datasetsFilter: '',
      datasetsFilteredList: [],
      datasetsAppliedFilter: null,

      showBeds: false,
      showUfs: false,
      showJh: false,
      showSea: false,
      showETypes: false,
      legacySelection: false,

      selectionDatasets: null,
      establishmentTypes: {}
    }
  },

  methods: {
    exportList: function () {
      const vm = this
      stringify(
        vm.finalFilteredList,
        {
          bom: true,
          header: true,
          delimiter: ';',
          columns: [...sc.csvColumns, ...buildYearCSVColumns(vm)]
        },

        function (err, output) {
          if (err != null) {
            vm.$notify({
              type: 'danger',
              title: 'Erreur !',
              content: `Impossible d'exporter les données: ${err}.`
            })
          } else {
            setTimeout(
              function () {
                vm.csv = {
                  filename: `SPIADI - COHORTES - Liste des services.csv`,
                  blob: new Blob([output], { type: 'text/plain' })
                }
              }, 10)
          }
        })
    },

    askForRemoval: function (e) {
      const vm = this
      confirm(
        vm,
        `Êtes vous sûr de vouloir retirer la ligne
${e.name}-${e.mod}-${e.disc} de la cohorte ?`
      ).then(
        () => {
          const and = vm.filter === '' ? '' : ' et '
          vm.filter += `${and}non ($n: "${e.name}" et $f: "${e.finess}" et $m: "${e.mod}" et $d: "${e.disc}")`
        }
      )
    },

    onDatasetListFiltered: function (e) {
      const { filter, lists } = e
      if (lists.length !== 1) {
        return
      }

      this.datasetsFilteredList = lists[0]
      this.datasetsAppliedFilter = filter
    },

    lockSelection: function () {
      if (this.finalFilteredList == null) {
        return
      }

      const promises = []
      const result = []
      const index = []
      for (let i = 0; i !== this.finalSelection.length; ++i) {
        const sel = this.finalSelection[i]
        if (sel.request != null) {
          promises.push(api.filterByDisc(sel.request))
          index.push(i)
        }

        result.push({
          name: sel.name,
          year: sel.year,
          survs: sel.survs,
          list: clone(sel.dsets)
        })
      }

      this.filterSheets(promises, index, result)
    },

    filterSheets: function (promises, index, result) {
      const vm = this

      function success (_, r) {
        for (let i = 0; i !== r.length; ++i) {
          const sheetsByMod = r[i].data
          const yd = result[index[i]].list

          for (let j = 0; j !== yd.length; ++j) {
            const dset = yd[j]
            const sheets = sheetsByMod[dset.mod]
            if (sheets == null) {
              dset.shev = []
            } else {
              dset.shev = dset.shev.filter(
                s => sheets.findIndex(sh => sh === s.id) !== -1
              )
            }
          }
        }
        vm.$nextTick(
          () => {
            vm.selectionDatasets = ice.freeze(result)
            const message = {
              selectionDatasets: vm.selectionDatasets,
              etypes: vm.establishmentTypes
            }
            vm.$emit('selected', message)
          }
        )
      }

      function error (e) {
        vm.$notify({
          type: 'danger',
          title: 'Erreur !',
          duration: 0,
          html: true,
          content: `Impossible de récupérer la liste des
fiches filtrées : <ul><li>${e.join('</li><li>')}</li></ul>`
        })
      }

      promise.awaitAll(promises, success, error)
    },

    getLastEstablishmentType: function (finess) {
      let cache = this.establishmentTypes[finess]
      if (cache != null) {
        return cache >= 0 ? cache : null
      }

      const years = Object.keys(this.all).sort((a, b) => parseInt(b) > parseInt(a))
      for (let i = 0; i !== years.length; ++i) {
        const y = years[i]
        const surv = this.all[y].find(s => s.establishment.finess === finess)
        if (surv != null && surv.establishment.type >= 0) {
          cache = surv.establishment.type
          this.establishmentTypes[finess] = cache
          return cache
        }
      }

      this.establishmentTypes[finess] = -1
      return null
    }
  },

  computed: {
    ...mapGetters({
      getDatasets: 'dataset/getDatasets'
    }),

    ...mapState({
      all: state => state.surveillances.all
    }),

    allDatasets: function () {
      if (this.data == null) {
        return null
      }

      const resultBySurv = {}
      const resultByFiness = {}
      const { dsets, mods } = this.data
      const headlessDset = new Set()
      for (let y in dsets) {
        const c = dsets[y]
        if (c === '') { continue }

        const survById = {}
        const allSurvs = this.all[y]

        for (let i = 0; i !== mods.length; ++i) {
          const m = mods[i]
          const list = this.getDatasets(c, m, 2)
          if (list == null) { continue }

          for (let j = 0; j !== list.length; ++j) {
            const d = list[j]
            let surv = survById[d.surv]
            if (surv == null) {
              surv = allSurvs.find(s => s.id === d.surv)
              if (surv == null) {
                headlessDset.add(d.surv)
                continue
              }
              assert.notNull(surv)
              survById[d.surv] = surv
            }

            for (let k = 0; k !== d.den.length; ++k) {
              const den = d.den[k]

              if (den.val <= 0) {
                continue
              }

              const [disc, spe, subSpe] = go.units.getIncidenceDisciplineAndSpeciality(den.ut)
              assert.notNull(disc)

              if (!subSpe) {
                continue
              }

              if (this.legacySelection) {
                if (disc.Index !== den.ut) {
                  if (m === 'bactADI' &&
                      disc.Index !== go.units.disciplines.AdRea.index &&
                      disc.Index !== go.units.disciplines.AdCan.index &&
                      disc.Index !== go.units.disciplines.AdRad.index &&
                      disc.Index !== go.units.disciplines.AdHem.index &&
                      disc.Index !== go.units.disciplines.PedCan.index &&
                      (parseInt(y) < 2024 || disc.Index !== go.units.disciplines.AdDia.index)) {
                    continue
                  }
                }
              }

              const key = `${surv.establishment.finess}-${m}-${disc.Index}`
              let target = resultBySurv[key]
              if (target == null) {
                target = buildTableLine(m, surv, disc, dsets)
                resultBySurv[key] = target
              }
              const uf = { uf: den.uf, ut: den.ut }

              const typeKey = buildYearlyTypeKey(y)
              const type = results.getEstablishementType(surv.establishment.type)

              const current = target[typeKey]
              if (current != null) {
                assert.equals(current, type)
              } else if (type != null) {
                target[typeKey] = type
              }

              const dsname = buildYearlyDatasetKey(y)
              if (target[dsname] == null) {
                target[dsname] = {
                  name: `${d.name}-COHORT`,
                  ref: d._key,
                  den: [],
                  mod: d.mod,
                  peri: d.peri,
                  shev: d.shev,
                  pneu: d.pneu,
                  seps: d.seps,
                  step: d.step,
                  surv: d.surv
                }
              }

              target[dsname].den.push(den)

              if (m === 'bactADI' && uf.uf !== '__all__') {
                continue
              }
              const t = target[`ufs${y}`]
              if (t.find(l => l.uf === uf.uf && l.ut === uf.ut) == null) {
                t.push(uf)

                const yk = `ufs${y}Display`
                const ufd = `${den.uf} (${spe != null ? spe.Code : disc.Code})`
                const old = target[yk]
                target[yk] = `${old}${old === '' ? '' : ', '}${ufd}`
              }

              if (sc.refByYear.indexOf(den.den) !== -1) {
                const jhk = `${den.den}${y}`
                const initial = target[jhk]
                if (initial == null) {
                  const fKey = `${surv.establishment.finess}-${disc.index}`
                  let byFiness = resultByFiness[fKey]
                  if (byFiness == null) {
                    byFiness = {
                      jh: new Set(),
                      sea: new Set(),
                      bed: new Set(),
                      jhByDisc: {},
                      seaByDisc: {},
                      bedByDisc: {}
                    }
                    resultByFiness[fKey] = byFiness
                  }
                  byFiness[den.den].add(y)
                  const byDisc = byFiness[`${den.den}ByDisc`]
                  let byDiscSet = byDisc[disc.Code]
                  if (byDiscSet == null) {
                    byDiscSet = new Set()
                    byDisc[disc.Code] = byDiscSet
                  }
                  byDiscSet.add(y)
                  target[`nb${den.den}`] += 1
                }
                target[jhk] = (initial || 0) + den.val
              }
            }
          }
        }
      }

      let result = []
      for (let k in resultBySurv) {
        const r = resultBySurv[k]
        if (r.nbjh > 0 || r.nbsea > 0) {
          r.type = results.getEstablishementType(
            this.getLastEstablishmentType(r.finess)
          ) || ''

          result.push(r)
          const key = `${r.finess}-${r.id}`
          const byFiness = resultByFiness[key]

          assert.notNull(byFiness, `missing key ${key}`)
          r.etbjh = byFiness.jh.size
          r.etbbed = byFiness.bed.size
          r.etbsea = byFiness.sea.size
          const jhByDisc = byFiness.jhByDisc[r.disc]
          const bedByDisc = byFiness.bedByDisc[r.disc]
          const seaByDisc = byFiness.seaByDisc[r.disc]
          r.discjh = jhByDisc != null ? jhByDisc.size : 0
          r.discbed = bedByDisc != null ? bedByDisc.size : 0
          r.discsea = seaByDisc != null ? seaByDisc.size : 0
        }
      }

      result = result.sort(
        (a, b) => {
          const t = a.town.localeCompare(b.town)
          if (t !== 0) {
            return t
          }

          const n = a.name.localeCompare(b.name)
          if (n !== 0) {
            return n
          }
          return a.mod.localeCompare(b.mod)
        }
      )

      const headlessList = Array.from(headlessDset)
      if (headlessList.length > 0) {
        this.$notify(
          {
            type: 'warning',
            title: 'Attention !',
            duration: 0,
            html: true,
            content: `${headlessList.length} jeux de données
sont liés à des surveillances détruites :<br>
<ul>
  <li>${headlessList.join('</li><li>')}</li>
</ul>`
          }
        )
      }
      return ice.freeze(result)
    },

    table: function () {
      const columns = [...sc.columns, ...buildYearColumns(this)]

      const filterVariables = [...sc.filterVariables, ...buildYearFilterVariables(this)]
      return ice.freeze(
        {
          columns: columns,
          actions: [{ event: 'remove', name: 'Retirer' }],
          keyfield: '',
          filterList: [
            {
              list: this.allDatasets,
              variables: filterVariables
            }
          ],
          helps: sc.helps
        }
      )
    },

    filterEvents: function () {
      return {
        filter: this.onDatasetListFiltered,
        csv: this.exportList
      }
    },

    filterSelectionInfo: function () {
      if (this.finalFilteredList == null) {
        return null
      }

      return {
        statistics: {
          count: this.finalFilteredList.length,
          total: this.allDatasets.length
        },

        filter: this.currentFilter
      }
    },

    finalFilteredList: function () {
      if (this.allDatasets == null) {
        return null
      }

      if (this.datasetsAppliedFilter != null) {
        return this.datasetsFilteredList
      }

      return this.allDatasets
    },

    finalSelection: function () {
      const result = []

      const { dsets } = this.data
      for (let year in dsets) {
        const request = {
          year: parseInt(year),
          sheets: {},
          discs: {}
        }
        const ydsets = []
        const dsetCache = {}
        const survs = new Set()

        for (let i = 0; i !== this.finalFilteredList.length; ++i) {
          const elt = this.finalFilteredList[i]
          const ds = elt[buildYearlyDatasetKey(year)]
          if (ds == null) {
            continue
          }

          survs.add(ds.surv)

          pushOrCreate(request.discs, ds.surv, elt.disc)

          let targetDset = dsetCache[ds.ref]
          if (targetDset == null) {
            targetDset = {
              den: ds.den,
              mod: ds.mod,
              name: ds.name,
              peri: ds.peri,
              shev: ds.shev,
              pneu: ds.pneu,
              seps: ds.seps,
              step: ds.step,
              surv: ds.surv
            }
            dsetCache[ds.ref] = targetDset

            pushOrCreate(request.sheets, elt.mod, ...ds.shev)
            ydsets.push(targetDset)
          } else {
            targetDset.den.push(...ds.den)
          }
        }

        result.push(
          {
            name: dsets[year],
            year: year,
            dsets: ydsets,
            survs: survs,
            request: (Object.keys(request.sheets).length > 0) ? request : null
          }
        )
      }
      return result
    }
  },

  components: {
    'sui-table': suiTable,
    'sui-checkbox': suiCheckbox,
    'sui-save-blob': suiSaveBlob,
    'sui-filter-bar': suiFilterBar
  }
}
</script>
<style scoped lang="less">
.filter-bar-padding {
  padding-bottom: 4rem;
}
</style>
